/**
 * race_circuit.ts — the GRAND CIRCUIT (capital wave phase 5, 2026-09-16): a
 * ~28 km outdoor hovercraft loop through different areas — the stadium
 * straight at the campus, a run along capital A's wall, a ridge climb, ten
 * switchbacks, a sweep along capital B's wall, open-country sweeps home.
 *
 * PURE and worker-safe (no THREE): the chunk worker composes the corridor
 * stamp through terrain_stamp; the client module draws the ribbon, fences it
 * and runs the lap HUD from the same samples. Deterministic: seed + the three
 * site positions in, the same loop out every time; a loop that breaks a rule
 * damps its amplitudes and retries, deterministically.
 *
 * Coordinates are WORLD XZ; heights are the graded corridor height (the
 * landform terrain low-passed over 300 m, grade-clamped at 7 %, the stadium
 * straight level on the campus plateau); `s` is arc length from the start line.
 */
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { terrainHeight, terrainHeightBase, plateauFor, PLATEAU_RADIUS } from '../../../shared/terrain.js';
import { CAPITAL_WALL_R, campusFrame, CAMPUS_STRAIGHT_OFF_M } from '../../../shared/capitals.js';

export const GRAND_CIRCUIT_TARGET_KM = 28;
export const TRACK_HALF_W = 15;
export const SHOULDER_M = 6;
export const BLEND_M = 10;
export const SAMPLE_M = 2;
export const MIN_RADIUS_M = 90;
export const MAX_GRADE = 0.07;
export const WALL_CLEAR_M = 250;
export const WALL_RUN_OFF_M = 380;
export const SITE_CLEAR_EXTRA_M = 480 + 40;
export const SECTORS = 12;
export const BIN_M = 256;
export const RETRIES = 8;
export const DAMP = 0.85;
export const STRAIGHT_OFFSET_M = CAMPUS_STRAIGHT_OFF_M;
export const STADIUM_STRAIGHT_M = 900;
export const BARRIER_SEG_M = 16;
export const LOWPASS_M = 300;
export const PROFILE_M = 10;
export const RIDGE_SEARCH_M = 9000;
export const RIDGE_LATTICE_M = 500;
export const RIDGE_MIN_FROM_CAMPUS_M = 1500;
export const SWITCHBACKS = 10;
/** 340 m (the spec's 260 m cannot hold a 90 m radius at any useful amplitude: κ = A·(2π/2·spacing)²). */
export const SWITCHBACK_SPACING_M = 340;
export const SWITCHBACK_AMP_MIN = 70;
export const SWITCHBACK_AMP_VAR = 40;
export const REPEL_MARGIN_M = 60;
/** The ridge is rounded: the corridor circles the summit at this radius from the incoming to the outgoing bearing. */
export const RIDGE_LOOP_R = 320;
/** Each wall run is entered and left along its tangent: a held vertex this far before its start and after its end. */
export const ARC_RUN_M = 400;
/** A fillet's second vertex sits this far along the bisector of the two directions it joins. */
export const FILLET_M = 500;
/** The wall runs sweep from the arrival bearing toward the next leg's bearing: at least this, at most WALL_RUN_DEG. */
export const WALL_RUN_MIN_DEG = 30;
export const WALL_RUN_DEG = 70;
export const SWEEP_SPACING_M = 900;

export interface CircuitPoint { x: number; z: number; y: number; s: number }
export interface CircuitSector { index: number; name: string; s0: number; s1: number; gate: { x: number; z: number; y: number; yaw: number } }
export interface CircuitLeg { name: string; s0: number; s1: number }
export interface Circuit {
  seed: string; site: SettlementSite; a: SettlementSite; b: SettlementSite;
  length: number; pts: CircuitPoint[]; sectors: CircuitSector[]; legs: CircuitLeg[];
  start: { x: number; z: number; yaw: number }; ridge: { x: number; z: number; h: number };
  attempts: number; damping: number; problems: string[];
}

const TAU = Math.PI * 2;

/** FNV-1a 0..1 over `${seed}|${salt}` (the same family as the site pickers). */
function h01(seed: string, salt: string): number {
  let h = 0x811c9dc5;
  const s = `${seed}|${salt}`;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return (h >>> 0) / 0xffffffff;
}

interface XZ { x: number; z: number }

// ── Curve: centripetal Catmull-Rom over a CLOSED control loop, resampled at 2 m ──
function catmullRomClosed(ctrl: XZ[], denseStep = 1): XZ[] {
  const n = ctrl.length;
  const out: XZ[] = [];
  const alpha = 0.5;
  for (let i = 0; i < n; i++) {
    const p0 = ctrl[(i - 1 + n) % n], p1 = ctrl[i], p2 = ctrl[(i + 1) % n], p3 = ctrl[(i + 2) % n];
    const tj = (ti: number, a: XZ, b: XZ): number => ti + Math.pow(Math.hypot(b.x - a.x, b.z - a.z), alpha);
    const t0 = 0, t1 = tj(t0, p0, p1), t2 = tj(t1, p1, p2), t3 = tj(t2, p2, p3);
    const chord = Math.hypot(p2.x - p1.x, p2.z - p1.z);
    const steps = Math.max(2, Math.ceil(chord / denseStep));
    for (let k = 0; k < steps; k++) {
      const t = t1 + ((t2 - t1) * k) / steps;
      const l = (a: XZ, b: XZ, ta: number, tb: number): XZ => {
        const w = tb - ta === 0 ? 0 : (t - ta) / (tb - ta);
        return { x: a.x + (b.x - a.x) * w, z: a.z + (b.z - a.z) * w };
      };
      const A1 = l(p0, p1, t0, t1), A2 = l(p1, p2, t1, t2), A3 = l(p2, p3, t2, t3);
      const B1 = l(A1, A2, t0, t2), B2 = l(A2, A3, t1, t3);
      const q = l(B1, B2, t1, t2);
      const prev = out[out.length - 1];
      if (!prev || Math.hypot(q.x - prev.x, q.z - prev.z) > 1e-6) out.push(q); // coincident controls never stall the resampler
    }
  }
  return out;
}

/** Resample a closed polyline at an exact spacing; returns points + total length. */
function resample(dense: XZ[], step: number): { pts: XZ[]; length: number } {
  const n = dense.length;
  let total = 0;
  for (let i = 0; i < n; i++) { const a = dense[i], b = dense[(i + 1) % n]; total += Math.hypot(b.x - a.x, b.z - a.z); }
  const count = Math.max(4, Math.round(total / step));
  const stepReal = total / count;
  const pts: XZ[] = [];
  let seg = 0, segStart = 0;
  let segLen = Math.hypot(dense[1 % n].x - dense[0].x, dense[1 % n].z - dense[0].z);
  for (let k = 0; k < count; k++) {
    const target = k * stepReal;
    while (target > segStart + segLen && seg < n - 1) { segStart += segLen; seg++; const a = dense[seg], b = dense[(seg + 1) % n]; segLen = Math.hypot(b.x - a.x, b.z - a.z); }
    const a = dense[seg], b = dense[(seg + 1) % n];
    const w = segLen > 0 ? (target - segStart) / segLen : 0;
    pts.push({ x: a.x + (b.x - a.x) * w, z: a.z + (b.z - a.z) * w });
  }
  return { pts, length: total };
}

// ── Layout ──────────────────────────────────────────────────────────────────
type CtrlKind = 'straight' | 'arc' | 'switch' | 'free' | 'detour';
interface Ctrl extends XZ { kind: CtrlKind }
interface Layout { ctrl: Ctrl[]; legStarts: Array<{ name: string; index: number }>; legVerts: Ctrl[]; straight: { s: XZ; p0: XZ; p1: XZ; t: XZ; n: XZ }; ridge: { x: number; z: number; h: number } }
/** The control polygon of the last build (the picture's debugging aid). */
export const lastControls: { ctrl: Array<{ x: number; z: number; kind: string }> } = { ctrl: [] };

function clearOfReservations(sites: SettlementSite[], campus: SettlementSite, x: number, z: number): boolean {
  for (const o of sites) {
    if (o.id === campus.id || o.tier === 'district') continue;
    const d = Math.hypot(o.centerX - x, o.centerZ - z);
    if (o.tier === 'capital') { if (d < CAPITAL_WALL_R + WALL_CLEAR_M) return false; continue; }
    if (d < PLATEAU_RADIUS[o.tier] + SITE_CLEAR_EXTRA_M) return false;
  }
  return true;
}

/** The nearest reservation a point violates (its centre, radius + margin) or null. */
function violated(sites: SettlementSite[], campus: SettlementSite, x: number, z: number): { cx: number; cz: number; r: number } | null {
  let worst: { cx: number; cz: number; r: number; slack: number } | null = null;
  for (const o of sites) {
    if (o.id === campus.id || o.tier === 'district') continue;
    const r = o.tier === 'capital' ? CAPITAL_WALL_R + WALL_CLEAR_M : PLATEAU_RADIUS[o.tier] + SITE_CLEAR_EXTRA_M;
    const slack = Math.hypot(o.centerX - x, o.centerZ - z) - r;
    if (slack < 0 && (!worst || slack < worst.slack)) worst = { cx: o.centerX, cz: o.centerZ, r, slack };
  }
  return worst;
}

/** Push a point radially out of the reservation it violates (to r + margin). */
function pushOut(v: { cx: number; cz: number; r: number }, x: number, z: number): XZ {
  const d = Math.hypot(x - v.cx, z - v.cz) || 1;
  const rr = v.r + REPEL_MARGIN_M;
  return { x: v.cx + ((x - v.cx) / d) * rr, z: v.cz + ((z - v.cz) / d) * rr };
}

/**
 * Keep the control polygon out of every reservation: a violating movable
 * vertex is pushed out; a violating span between two vertices gets a pushed
 * midpoint inserted (Catmull-Rom passes through its controls, so a dense
 * detour keeps the curve out too). Six rounds, deterministic.
 */
function repelFromReservations(ctrl: Ctrl[], sites: SettlementSite[], campus: SettlementSite): void {
  for (let round = 0; round < 6; round++) {
    let changed = false;
    for (let i = 0; i < ctrl.length; i++) {
      const c = ctrl[i];
      if (c.kind === 'free' || c.kind === 'switch' || c.kind === 'detour') {
        const v = violated(sites, campus, c.x, c.z);
        if (v) { const q = pushOut(v, c.x, c.z); c.x = q.x; c.z = q.z; changed = true; }
      }
    }
    for (let i = 0; i < ctrl.length; i++) {
      const a = ctrl[i], b = ctrl[(i + 1) % ctrl.length];
      if (a.kind === 'straight' && b.kind === 'straight') continue;
      if (a.kind === 'arc' && b.kind === 'arc') continue;
      for (let k = 1; k <= 4; k++) {
        const t = k / 5;
        const x = a.x + (b.x - a.x) * t, z = a.z + (b.z - a.z) * t;
        const v = violated(sites, campus, x, z);
        if (v) { const q = pushOut(v, x, z); ctrl.splice(i + 1, 0, { x: q.x, z: q.z, kind: 'free' }); changed = true; break; }
      }
    }
    if (!changed) return;
  }
}

/** Round the leg joins: three Laplacian rounds over the FREE vertices (the straight, the wall arcs and the switchbacks hold). */
function smoothCorners(ctrl: Ctrl[]): void {
  for (let round = 0; round < 3; round++) {
    const src = ctrl.map((c) => ({ x: c.x, z: c.z }));
    for (let i = 0; i < ctrl.length; i++) {
      if (ctrl[i].kind !== 'free') continue;
      const a = src[(i - 1 + ctrl.length) % ctrl.length], b = src[(i + 1) % ctrl.length];
      ctrl[i].x = src[i].x * 0.5 + (a.x + b.x) * 0.25;
      ctrl[i].z = src[i].z * 0.5 + (a.z + b.z) * 0.25;
    }
  }
}

function findRidge(seed: string, sites: SettlementSite[], campus: SettlementSite, a: SettlementSite, b: SettlementSite): { x: number; z: number; h: number } {
  let best: { x: number; z: number; h: number; score: number } | null = null;
  // The climb lies BETWEEN the two wall runs on the FAR side of the A–B line from the
  // campus, so the loop runs campus → A → ridge → B → campus without crossing itself.
  const abx = b.centerX - a.centerX, abz = b.centerZ - a.centerZ, abL = Math.hypot(abx, abz) || 1;
  const sideC = Math.sign((campus.centerX - a.centerX) * abz - (campus.centerZ - a.centerZ) * abx) || 1;
  const n = Math.ceil(RIDGE_SEARCH_M / RIDGE_LATTICE_M);
  for (let i = -n; i <= n; i++) for (let j = -n; j <= n; j++) {
    const x = campus.centerX + i * RIDGE_LATTICE_M, z = campus.centerZ + j * RIDGE_LATTICE_M;
    const dc = Math.hypot(x - campus.centerX, z - campus.centerZ);
    if (dc > RIDGE_SEARCH_M || dc < RIDGE_MIN_FROM_CAMPUS_M) continue;
    if (!clearOfReservations(sites, campus, x, z)) continue;
    // Room for the legs: the switchbacks need their whole run before B's wall run, the
    // climb a real approach after A's.
    const RW = CAPITAL_WALL_R + WALL_RUN_OFF_M;
    if (Math.hypot(x - b.centerX, z - b.centerZ) < RW + (SWITCHBACKS + 1) * SWITCHBACK_SPACING_M + 600) continue;
    if (Math.hypot(x - a.centerX, z - a.centerZ) < RW + 1500) continue;
    const side = (x - a.centerX) * abz - (z - a.centerZ) * abx;
    if (Math.sign(side) === sideC) continue; // the campus's own side of the line
    const h = terrainHeightBase(seed, x, z);
    const off = Math.abs(side) / abL;
    const score = h - 0.001 * off;
    if (!best || score > best.score) best = { x, z, h, score };
  }
  return best ?? { x: campus.centerX + RIDGE_MIN_FROM_CAMPUS_M, z: campus.centerZ, h: terrainHeightBase(seed, campus.centerX + RIDGE_MIN_FROM_CAMPUS_M, campus.centerZ) };
}

function layout(seed: string, sites: SettlementSite[], campus: SettlementSite, a: SettlementSite, b: SettlementSite, damping: number): Layout {
  const C = { x: campus.centerX, z: campus.centerZ };
  // t̂ toward capital A; the straight stands on B's side so the run home arrives without crossing the campus.
  const { t, n } = campusFrame(C.x, C.z, a.centerX, a.centerZ, b.centerX, b.centerZ);
  const S = { x: C.x + n.x * STRAIGHT_OFFSET_M, z: C.z + n.z * STRAIGHT_OFFSET_M };
  const half = STADIUM_STRAIGHT_M / 2;
  const p0 = { x: S.x - t.x * half, z: S.z - t.z * half };
  const p1 = { x: S.x + t.x * half, z: S.z + t.z * half };
  const ctrl: Ctrl[] = [];
  const legStarts: Array<{ name: string; index: number }> = [];
  const leg = (name: string): void => { legStarts.push({ name, index: ctrl.length }); };
  const push = (kind: CtrlKind, ...pts: XZ[]): void => { for (const q of pts) ctrl.push({ x: q.x, z: q.z, kind }); };
  leg('Stadium Straight');
  push('straight', S, { x: S.x + t.x * half * 0.5, z: S.z + t.z * half * 0.5 });
  // ── Skyline Approach: from the straight's end to A's wall ──
  leg('Skyline Approach');
  push('straight', p1);
  const norm = (v: XZ): XZ => { const L = Math.hypot(v.x, v.z) || 1; return { x: v.x / L, z: v.z / L }; };
  const spanOf = (gap: number): number => Math.max((WALL_RUN_MIN_DEG * Math.PI) / 180, Math.min((WALL_RUN_DEG * Math.PI) / 180, Math.abs(gap) - (15 * Math.PI) / 180));
  /** Held vertices that turn a leg heading `dFrom` onto an arc whose tangent at its start is `tan`:
   *  a point on the bisector, then a run-in along the tangent — three segments of ≤ 45°. */
  const filletIn = (arcStart: XZ, tan: XZ, from: XZ): XZ[] => {
    const runIn = { x: arcStart.x - tan.x * ARC_RUN_M, z: arcStart.z - tan.z * ARC_RUN_M };
    const dApp = norm({ x: runIn.x - from.x, z: runIn.z - from.z });
    const bis = norm({ x: dApp.x + tan.x, z: dApp.z + tan.z });
    return [{ x: runIn.x - bis.x * FILLET_M, z: runIn.z - bis.z * FILLET_M }, runIn];
  };
  /** …and the mirror on the way out: a run-out along the tangent, then a point on the bisector toward `to`. */
  const filletOut = (arcEnd: XZ, tan: XZ, to: XZ): XZ[] => {
    const runOut = { x: arcEnd.x + tan.x * ARC_RUN_M, z: arcEnd.z + tan.z * ARC_RUN_M };
    const dDep = norm({ x: to.x - runOut.x, z: to.z - runOut.z });
    const bis = norm({ x: dDep.x + tan.x, z: dDep.z + tan.z });
    return [runOut, { x: runOut.x + bis.x * FILLET_M, z: runOut.z + bis.z * FILLET_M }];
  };
  /** Is the straight line a→b (sampled every 100 m) clear of every reservation? */
  const lineClear = (a0: XZ, b0: XZ): boolean => {
    const L = Math.hypot(b0.x - a0.x, b0.z - a0.z);
    const n = Math.max(2, Math.ceil(L / 100));
    for (let k = 0; k <= n; k++) { const t = k / n; if (!clearOfReservations(sites, campus, a0.x + (b0.x - a0.x) * t, a0.z + (b0.z - a0.z) * t)) return false; }
    return true;
  };
  /** A wall run from phi0 toward phi1: the LARGEST span (down to the minimum, 5° steps) whose
   *  exit fillet + the line on to `to` clear every reservation — the run ends where the road can leave. */
  const arcOn = (cx: number, cz: number, r: number, phi0: number, phi1: number, to: XZ): { pts: XZ[]; dir: 1 | -1; span: number; t0: XZ; t1: XZ } => {
    let gap = phi1 - phi0; while (gap > Math.PI) gap -= TAU; while (gap < -Math.PI) gap += TAU;
    const dir: 1 | -1 = gap >= 0 ? 1 : -1;
    const tanAt = (ang: number): XZ => ({ x: -Math.sin(ang) * dir, z: Math.cos(ang) * dir });
    const full = spanOf(gap), min = (WALL_RUN_MIN_DEG * Math.PI) / 180;
    let span = full;
    for (let sp = full; sp >= min - 1e-9; sp -= (5 * Math.PI) / 180) {
      const end = { x: cx + Math.cos(phi0 + dir * sp) * r, z: cz + Math.sin(phi0 + dir * sp) * r };
      const f = filletOut(end, tanAt(phi0 + dir * sp), to);
      if (lineClear(end, f[0]) && lineClear(f[0], f[1]) && lineClear(f[1], to)) { span = sp; break; }
      span = sp;
    }
    const pts: XZ[] = [];
    for (let k = 0; k <= 6; k++) { const ang = phi0 + dir * span * (k / 6); pts.push({ x: cx + Math.cos(ang) * r, z: cz + Math.sin(ang) * r }); }
    return { pts, dir, span, t0: tanAt(phi0), t1: tanAt(phi0 + dir * span) };
  };
  const RA = CAPITAL_WALL_R + WALL_RUN_OFF_M, RB = RA;
  const ridge = findRidge(seed, sites, campus, a, b);
  const R = { x: ridge.x, z: ridge.z };
  // A: from the campus-facing point toward the ridge's bearing.
  const A = arcOn(a.centerX, a.centerZ, RA, Math.atan2(C.z - a.centerZ, C.x - a.centerX), Math.atan2(R.z - a.centerZ, R.x - a.centerX), R);
  const inA = filletIn(A.pts[0], A.t0, p1);
  push('free', { x: (p1.x * 2 + inA[0].x) / 3, z: (p1.z * 2 + inA[0].z) / 3 }, { x: (p1.x + inA[0].x * 2) / 3, z: (p1.z + inA[0].z * 2) / 3 });
  push('switch', ...inA);
  // ── Skyline Run ──
  leg('Skyline Run');
  push('arc', ...A.pts);
  const outA = filletOut(A.pts[6], A.t1, R);
  push('switch', ...outA);
  // ── Ridge Climb: to the summit and around it (a RIDGE_LOOP_R arc from the incoming to the outgoing bearing, the shorter way, filleted) ──
  leg('Ridge Climb');
  const phiRB = Math.atan2(R.z - b.centerZ, R.x - b.centerX);
  const arcBStart = { x: b.centerX + Math.cos(phiRB) * RB, z: b.centerZ + Math.sin(phiRB) * RB };
  const from = outA[1];
  const angIn = Math.atan2(from.z - R.z, from.x - R.x);
  const angOut = Math.atan2(arcBStart.z - R.z, arcBStart.x - R.x);
  let dAng = angOut - angIn; while (dAng > Math.PI) dAng -= TAU; while (dAng < -Math.PI) dAng += TAU;
  const ldir: 1 | -1 = dAng >= 0 ? 1 : -1;
  const lsteps = Math.max(2, Math.ceil(Math.abs(dAng) / (Math.PI / 4)));
  const loopPts: XZ[] = [];
  for (let k = 0; k <= lsteps; k++) { const ang = angIn + (dAng * k) / lsteps; loopPts.push({ x: R.x + Math.cos(ang) * RIDGE_LOOP_R, z: R.z + Math.sin(ang) * RIDGE_LOOP_R }); }
  const lt0 = { x: -Math.sin(angIn) * ldir, z: Math.cos(angIn) * ldir }, lt1 = { x: -Math.sin(angIn + dAng) * ldir, z: Math.cos(angIn + dAng) * ldir };
  const inL = filletIn(loopPts[0], lt0, from);
  push('free', { x: (from.x * 2 + inL[0].x) / 3, z: (from.z * 2 + inL[0].z) / 3 }, { x: (from.x + inL[0].x * 2) / 3, z: (from.z + inL[0].z * 2) / 3 });
  push('switch', ...inL, ...loopPts);
  const outL = filletOut(loopPts[loopPts.length - 1], lt1, arcBStart);
  push('switch', ...outL);
  // ── Switchbacks: along the line from the ridge to B's wall, ending a fillet short of it ──
  leg('Switchbacks');
  const B = arcOn(b.centerX, b.centerZ, RB, phiRB, Math.atan2(C.z - b.centerZ, C.x - b.centerX), p0);
  const inB = filletIn(B.pts[0], B.t0, outL[1]);
  const exit = outL[1];
  const dSB = Math.hypot(inB[0].x - exit.x, inB[0].z - exit.z);
  const ds = norm({ x: inB[0].x - exit.x, z: inB[0].z - exit.z });
  const ns = { x: -ds.z, z: ds.x };
  for (let k = 1; k <= SWITCHBACKS; k++) {
    const along = k * SWITCHBACK_SPACING_M;
    if (along > dSB - 400) break;
    const amp = (SWITCHBACK_AMP_MIN + h01(seed, `sw${k}`) * SWITCHBACK_AMP_VAR) * damping;
    const sgn = k % 2 === 0 ? 1 : -1;
    push('switch', { x: exit.x + ds.x * along + ns.x * amp * sgn, z: exit.z + ds.z * along + ns.z * amp * sgn });
  }
  push('switch', ...inB);
  // ── Crown Sweep ──
  leg('Crown Sweep');
  push('arc', ...B.pts);
  const outB = filletOut(B.pts[6], B.t1, p0);
  push('switch', ...outB);
  // ── Landscape Run: gentle sweeps home to the straight's start ──
  leg('Landscape Run');
  const bEnd = outB[1];
  const dH = Math.hypot(p0.x - bEnd.x, p0.z - bEnd.z);
  const dh = norm({ x: p0.x - bEnd.x, z: p0.z - bEnd.z });
  const nh = { x: -dh.z, z: dh.x };
  let k = 1;
  for (let along = SWEEP_SPACING_M; along < dH - SWEEP_SPACING_M; along += SWEEP_SPACING_M, k++) {
    const amp = (200 + h01(seed, `sweep${k}`) * 100) * damping;
    const sgn = k % 2 === 0 ? 1 : -1;
    push('free', { x: bEnd.x + dh.x * along + nh.x * amp * sgn, z: bEnd.z + dh.z * along + nh.z * amp * sgn });
  }
  leg('Final Approach');
  push('straight', p0, { x: S.x - t.x * half * 0.5, z: S.z - t.z * half * 0.5 });
  // Legs are named by their FIRST vertex; repulsion inserts vertices AFTER a leg's start, so the
  // leg indices must be re-derived: remember each leg's first vertex object.
  const legVerts = legStarts.map((ls) => ctrl[ls.index]);
  // Smooth FIRST, then repel: the Laplacian pulls a pushed vertex back toward its neighbours.
  smoothCorners(ctrl);
  repelFromReservations(ctrl, sites, campus);
  legStarts.forEach((ls, k) => { ls.index = ctrl.indexOf(legVerts[k]); });
  return { ctrl, legStarts, legVerts, straight: { s: S, p0, p1, t, n }, ridge };
}

// ── Heights: low-passed landform terrain, grade-clamped, level on the stadium disc ──
function gradeHeights(seed: string, pts: XZ[], step: number, straight: Layout['straight'], plateauH: number | null): number[] {
  const n = pts.length;
  // Profile every PROFILE_M (the landform field is ~80 µs a sample), interpolated to every sample.
  const every = Math.max(1, Math.round(PROFILE_M / step));
  const prof: number[] = [];
  for (let i = 0; i < n; i += every) prof.push(terrainHeight(seed, pts[i].x, pts[i].z));
  const m = prof.length;
  const raw = new Array<number>(n);
  for (let i = 0; i < n; i++) { const f = i / every; const j = Math.floor(f), w = f - j; raw[i] = prof[j % m] + (prof[(j + 1) % m] - prof[j % m]) * w; }
  // Low-pass over LOWPASS_M (a closed moving average).
  const half = Math.round(LOWPASS_M / step / 2);
  const lp = new Array<number>(n);
  let sum = 0;
  for (let k = -half; k <= half; k++) sum += raw[(k + n) % n];
  for (let i = 0; i < n; i++) { lp[i] = sum / (2 * half + 1); sum += raw[(i + half + 1) % n] - raw[(i - half + n) % n]; }
  // The stadium straight sits level on the campus plateau.
  const isStraight = (p: XZ): boolean => {
    const u = (p.x - straight.s.x) * straight.t.x + (p.z - straight.s.z) * straight.t.z;
    const v = (p.x - straight.s.x) * straight.n.x + (p.z - straight.s.z) * straight.n.z;
    return Math.abs(u) <= STADIUM_STRAIGHT_M / 2 - 50 && Math.abs(v) <= 10; // the level part stops 50 m short of each end (the curve bends into the next leg)
  };
  const anchored = pts.map((q) => plateauH !== null && isStraight(q));
  const anchor = (): void => { if (plateauH !== null) for (let i = 0; i < n; i++) if (anchored[i]) lp[i] = plateauH; };
  anchor();
  // Grade clamp (cut AND fill): closed passes each way, the straight re-anchored after
  // every pass so the ramps meet the level stadium at ≤ 7 % — converges in a few rounds.
  const maxD = MAX_GRADE * step;
  for (let pass = 0; pass < 6; pass++) {
    for (let i = 1; i <= n; i++) { const a = (i - 1) % n, c = i % n; if (!anchored[c]) lp[c] = Math.min(lp[a] + maxD, Math.max(lp[a] - maxD, lp[c])); }
    for (let i = n - 1; i >= -1; i--) { const a = (i + 1 + n) % n, c = (i + n) % n; if (!anchored[c]) lp[c] = Math.min(lp[a] + maxD, Math.max(lp[a] - maxD, lp[c])); }
    anchor();
  }
  return lp;
}

// ── Validation ──────────────────────────────────────────────────────────────
function circumradius(a: XZ, b: XZ, c: XZ): number {
  const ab = Math.hypot(b.x - a.x, b.z - a.z), bc = Math.hypot(c.x - b.x, c.z - b.z), ca = Math.hypot(a.x - c.x, a.z - c.z);
  const cross = Math.abs((b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x));
  return cross < 1e-9 ? Infinity : (ab * bc * ca) / (2 * cross);
}

export function circuitValidation(c: Circuit): string[] {
  const out: string[] = [];
  const pts = c.pts, n = pts.length;
  const win = Math.round(10 / SAMPLE_M); // 20 m windows
  let minR = Infinity, maxG = 0, atR = 0, atG = 0;
  for (let i = 0; i < n; i++) {
    const a = pts[(i - win + n) % n], b = pts[i], d = pts[(i + win) % n];
    const r = circumradius(a, b, d);
    if (r < minR) { minR = r; atR = b.s; }
    const g = Math.abs(pts[(i + 1) % n].y - b.y) / SAMPLE_M;
    if (g > maxG) { maxG = g; atG = b.s; }
  }
  const legOf = (s: number): string => c.legs.find((l) => s >= l.s0 && s < l.s1)?.name ?? '?';
  if (minR < MIN_RADIUS_M) out.push(`min-radius ${minR.toFixed(0)} < ${MIN_RADIUS_M} at s ${atR.toFixed(0)} (${legOf(atR)})`);
  if (maxG > MAX_GRADE + 1e-6) out.push(`grade ${(maxG * 100).toFixed(1)} % > ${MAX_GRADE * 100} % at s ${atG.toFixed(0)} (${legOf(atG)})`);
  // Self-intersection at full width: any two samples ≥ 30 samples apart closer than 2 × TRACK_HALF_W.
  const cell = 64;
  const bins = new Map<number, number[]>();
  const key = (x: number, z: number): number => Math.floor(x / cell) * 1_000_003 + Math.floor(z / cell);
  pts.forEach((p, i) => { const k = key(p.x, p.z); let arr = bins.get(k); if (!arr) { arr = []; bins.set(k, arr); } arr.push(i); });
  let crossings = 0, firstCross = '';
  const minSep = 2 * TRACK_HALF_W;
  outer: for (let i = 0; i < n; i++) {
    const p = pts[i];
    const cx = Math.floor(p.x / cell), cz = Math.floor(p.z / cell);
    for (let dx = -1; dx <= 1; dx++) for (let dz = -1; dz <= 1; dz++) {
      const arr = bins.get((cx + dx) * 1_000_003 + (cz + dz));
      if (!arr) continue;
      for (const j of arr) {
        const gap = Math.min(Math.abs(i - j), n - Math.abs(i - j));
        if (gap < 30) continue;
        if (Math.hypot(pts[j].x - p.x, pts[j].z - p.z) < minSep) { if (crossings === 0) firstCross = `s ${p.s.toFixed(0)} (${legOf(p.s)}) × s ${pts[j].s.toFixed(0)} (${legOf(pts[j].s)})`; crossings++; if (crossings > 20) break outer; }
      }
    }
  }
  if (crossings > 0) out.push(`self-intersection (${crossings} sample pairs within ${minSep} m, first ${firstCross})`);
  const sites = settlementSites(c.seed);
  let unclear = 0, inWorld = true, firstBad = '';
  for (let i = 0; i < n; i += 5) { const p = pts[i]; if (!clearOfReservations(sites, c.site, p.x, p.z)) { unclear++; if (!firstBad) firstBad = `s ${p.s.toFixed(0)} (${legOf(p.s)})`; } if (Math.abs(p.x) > 1_000_000 || Math.abs(p.z) > 1_000_000) inWorld = false; }
  if (unclear > 0) out.push(`reservation clearance (${unclear} samples, first at ${firstBad})`);
  if (!inWorld) out.push('outside the world');
  const km = c.length / 1000;
  if (km < 22 || km > 34) out.push(`length ${km.toFixed(1)} km outside 22–34`);
  return out;
}

// ── The build ───────────────────────────────────────────────────────────────
const memo = new Map<string, Circuit | null>();
/** 'sync' (workers, node, bakes): a miss builds on the spot (~2 s). 'async' (the main
 *  thread): a miss returns null until `adoptCircuit` lands the worker's build — the
 *  stamp and mask self-heal like un-primed settlement plans. */
let policy: 'sync' | 'async' = 'sync';
export function setCircuitBuildPolicy(p: 'sync' | 'async'): void { policy = p; }

export function raceCircuit(seed: string): Circuit | null {
  if (memo.has(seed)) return memo.get(seed)!;
  if (policy === 'async') return null;
  const c = build(seed);
  memo.set(seed, c);
  return c;
}
/** The circuit if it is already built here (never builds). */
export function peekCircuit(seed: string): Circuit | null { return memo.get(seed) ?? null; }
/** Adopt a circuit built elsewhere (a worker) into this thread's memo; the bins rebuild lazily. */
export function adoptCircuit(c: Circuit): void { memo.set(c.seed, c); binMemo.delete(c.seed); }

function build(seed: string): Circuit | null {
  const sites = settlementSites(seed);
  const campus = sites.find((s) => s.tier === 'circuit');
  const caps = sites.filter((s) => s.tier === 'capital');
  if (!campus || caps.length < 2) return null;
  const [a, b] = caps;
  const plateau = plateauFor(seed, campus.id);
  let damping = 1;
  let last: Circuit | null = null;
  for (let attempt = 1; attempt <= RETRIES; attempt++) {
    const L = layout(seed, sites, campus, a, b, damping);
    // Curve-based detours: the sampled curve overshoots its control polygon, so a
    // reservation the polygon clears can still be cut. Any run of violating samples
    // gets its middle pushed out and inserted as a held control vertex; six rounds.
    let dense = catmullRomClosed(L.ctrl, 1);
    let rs = resample(dense, SAMPLE_M);
    for (let round = 0; round < 6; round++) {
      const runs: Array<{ i0: number; i1: number }> = [];
      let start = -1;
      for (let i = 0; i < rs.pts.length; i += 3) {
        const bad = !clearOfReservations(sites, campus, rs.pts[i].x, rs.pts[i].z);
        if (bad && start < 0) start = i;
        if (!bad && start >= 0) { runs.push({ i0: start, i1: i }); start = -1; }
      }
      if (start >= 0) runs.push({ i0: start, i1: rs.pts.length - 1 });
      if (runs.length === 0) break;
      for (const r of runs) {
        const mid = rs.pts[Math.floor((r.i0 + r.i1) / 2)];
        const v = violated(sites, campus, mid.x, mid.z);
        if (!v) continue;
        // A BYPASS ARC around the reservation (radius r + 80) from the bearing where the
        // curve entered it to the bearing where it left, the shorter way — a smooth
        // detour of radius ≥ 600 m instead of one pushed vertex and a kink.
        const pin = rs.pts[r.i0], pout = rs.pts[Math.min(r.i1, rs.pts.length - 1)];
        const angIn = Math.atan2(pin.z - v.cz, pin.x - v.cx), angOut = Math.atan2(pout.z - v.cz, pout.x - v.cx);
        let dAng = angOut - angIn; while (dAng > Math.PI) dAng -= TAU; while (dAng < -Math.PI) dAng += TAU;
        const rr = v.r + 80;
        const steps = Math.max(2, Math.ceil(Math.abs(dAng) / (Math.PI / 6)));
        const arc: XZ[] = [];
        for (let k = 0; k <= steps; k++) { const ang = angIn + (dAng * k) / steps; arc.push({ x: v.cx + Math.cos(ang) * rr, z: v.cz + Math.sin(ang) * rr }); }
        // Drop the free/detour vertices the bypass replaces (inside the circle + 300 m), then
        // insert the arc after the control vertex whose span is nearest the crossing.
        for (let i = L.ctrl.length - 1; i >= 0; i--) {
          const c0 = L.ctrl[i];
          if (!(c0.kind === 'free' || c0.kind === 'detour') || Math.hypot(c0.x - v.cx, c0.z - v.cz) >= rr + 300) continue;
          // A leg's first vertex hands its name to the next vertex before it goes.
          const lk = L.legVerts.indexOf(c0);
          if (lk >= 0) L.legVerts[lk] = L.ctrl[(i + 1) % L.ctrl.length];
          L.ctrl.splice(i, 1);
        }
        let bi = 0, bd = Infinity;
        for (let i = 0; i < L.ctrl.length; i++) {
          const c0 = L.ctrl[i], c1 = L.ctrl[(i + 1) % L.ctrl.length];
          const ex = c1.x - c0.x, ez = c1.z - c0.z, L2 = ex * ex + ez * ez;
          const t = L2 > 0 ? Math.max(0, Math.min(1, ((mid.x - c0.x) * ex + (mid.z - c0.z) * ez) / L2)) : 0;
          const d = Math.hypot(c0.x + ex * t - mid.x, c0.z + ez * t - mid.z);
          if (d < bd) { bd = d; bi = i; }
        }
        L.ctrl.splice(bi + 1, 0, ...arc.map((q) => ({ x: q.x, z: q.z, kind: 'detour' as CtrlKind })));
      }
      dense = catmullRomClosed(L.ctrl, 1);
      rs = resample(dense, SAMPLE_M);
    }
    L.legStarts.forEach((ls, k) => { ls.index = L.ctrl.indexOf(L.legVerts[k]); }); // detours shift the indices
    lastControls.ctrl = L.ctrl.map((c) => ({ x: c.x, z: c.z, kind: c.kind }));
    const { pts: xz, length } = rs;
    const heights = gradeHeights(seed, xz, SAMPLE_M, L.straight, plateau ? plateau.h : null);
    const pts: CircuitPoint[] = xz.map((p, i) => ({ x: p.x, z: p.z, y: heights[i], s: i * SAMPLE_M }));
    // Legs by the control points' nearest samples.
    const legs: CircuitLeg[] = L.legStarts.map((ls, k) => {
      const cp = L.ctrl[ls.index];
      let bi = 0, bd = Infinity;
      for (let i = 0; i < pts.length; i += 5) { const d = Math.hypot(pts[i].x - cp.x, pts[i].z - cp.z); if (d < bd) { bd = d; bi = i; } }
      return { name: ls.name, s0: pts[bi].s, s1: 0, k };
    }).map((l, k, arr) => ({ name: l.name, s0: k === 0 ? 0 : l.s0, s1: k + 1 < arr.length ? arr[k + 1].s0 : length }));
    const sectors: CircuitSector[] = [];
    for (let k = 0; k < SECTORS; k++) {
      const s0 = (length * k) / SECTORS, s1 = (length * (k + 1)) / SECTORS;
      const i = Math.min(pts.length - 1, Math.ceil(s0 / SAMPLE_M)); // the gate stands AT or just after its sector's start (never a hair before it)
      const p = pts[i], q = pts[(i + 1) % pts.length];
      const legName = legs.find((l) => s0 >= l.s0 && s0 < l.s1)?.name ?? legs[0].name;
      sectors.push({ index: k, name: `S${k + 1} · ${legName}`, s0, s1, gate: { x: p.x, z: p.z, y: p.y, yaw: Math.atan2(q.x - p.x, q.z - p.z) } });
    }
    const start = { x: pts[0].x, z: pts[0].z, yaw: Math.atan2(pts[1].x - pts[0].x, pts[1].z - pts[0].z) };
    const c: Circuit = { seed, site: campus, a, b, length, pts, sectors, legs, start, ridge: L.ridge, attempts: attempt, damping, problems: [] };
    c.problems = circuitValidation(c);
    last = c;
    if (c.problems.length === 0) return c;
    // Deterministic repair: damp the switchback / sweep amplitudes and try again.
    damping *= DAMP;
  }
  return last; // the last attempt with its problems — the harness names them
}

// ── Spatial bins over the samples ───────────────────────────────────────────
interface Bins { cell: number; map: Map<number, number[]>; c: Circuit }
const binMemo = new Map<string, Bins | null>();
function binsFor(seed: string): Bins | null {
  if (binMemo.has(seed)) return binMemo.get(seed)!;
  const c = raceCircuit(seed);
  if (!c) { binMemo.set(seed, null); return null; }
  const map = new Map<number, number[]>();
  c.pts.forEach((p, i) => { const k = Math.floor(p.x / BIN_M) * 1_000_003 + Math.floor(p.z / BIN_M); let arr = map.get(k); if (!arr) { arr = []; map.set(k, arr); } arr.push(i); });
  const b = { cell: BIN_M, map, c };
  binMemo.set(seed, b);
  return b;
}

/** Nearest corridor sample within `reachCells` bins: distance to the polyline, arc length, sample index. */
export function circuitDist(seed: string, wx: number, wz: number, reachCells = 1): { d: number; s: number; i: number; y: number } | null {
  const b = binsFor(seed);
  if (!b) return null;
  const cx = Math.floor(wx / b.cell), cz = Math.floor(wz / b.cell);
  const pts = b.c.pts, n = pts.length;
  let best: { d: number; s: number; i: number; y: number } | null = null;
  for (let dx = -reachCells; dx <= reachCells; dx++) for (let dz = -reachCells; dz <= reachCells; dz++) {
    const arr = b.map.get((cx + dx) * 1_000_003 + (cz + dz));
    if (!arr) continue;
    for (const i of arr) {
      const p = pts[i], q = pts[(i + 1) % n];
      const ex = q.x - p.x, ez = q.z - p.z;
      const L2 = ex * ex + ez * ez;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((wx - p.x) * ex + (wz - p.z) * ez) / L2)) : 0;
      const d = Math.hypot(p.x + ex * t - wx, p.z + ez * t - wz);
      if (!best || d < best.d) best = { d, s: p.s + t * SAMPLE_M, i, y: p.y + (q.y - p.y) * t };
    }
  }
  return best;
}

function smooth(a: number, b: number, x: number): number { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

/** The corridor stamp: the track + shoulder LEVEL at the graded height, blended back over BLEND_M. */
export function circuitHeight(h: number, seed: string, wx: number, wz: number): number {
  const q = circuitDist(seed, wx, wz);
  if (!q) return h;
  const flat = TRACK_HALF_W + SHOULDER_M;
  if (q.d <= flat) return q.y;
  if (q.d >= flat + BLEND_M) return h;
  const w = 1 - smooth(flat, flat + BLEND_M, q.d);
  return h + (q.y - h) * w;
}

/** 1 on the track, fading to 0 three metres past its edge (the tint + flora rejection). */
export function circuitMask(seed: string, wx: number, wz: number): number {
  const q = circuitDist(seed, wx, wz);
  if (!q) return 0;
  return 1 - smooth(TRACK_HALF_W, TRACK_HALF_W + 3, q.d);
}

/** Arc length of the nearest sample within ~500 m, or null (the lap HUD's progress). */
export function progressAlong(seed: string, x: number, z: number): { s: number; d: number } | null {
  const q = circuitDist(seed, x, z, 2);
  return q ? { s: q.s, d: q.d } : null;
}

/** The sector an arc length falls in. */
export function sectorAt(c: Circuit, s: number): CircuitSector {
  const k = Math.min(SECTORS - 1, Math.max(0, Math.floor((((s % c.length) + c.length) % c.length) / (c.length / SECTORS))));
  return c.sectors[k];
}
